import classNames from "classnames";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { FileIcon } from "components/design/FileIcon";
import { GreentickGreenCircle } from "components/icons/GreentickGreenCircle";
import Prevfolder from "static/images/Prevfolder.svg";
import { useState } from "react";
import {
  useGetFolderContentQuery,
  useGetTagDocumentsQuery,
} from "store/apis/documents";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useToast } from "hooks/useToast";
import { BackendError } from "types/utils/error";
import { FOLDER_TYPE } from "constants/documents";
import { SelecteFile } from "types/Models/documents";
import { ModalProps } from "types/utils/modal";

type Props = {
  groupId?: string;
  alreadySelectedUuids?: string[];
  onInsert: (files: SelecteFile[]) => void;
} & ModalProps;

export const Form1120DocSelectorModal = ({
  isOpen,
  close,
  groupId: groupIdFromProps,
  alreadySelectedUuids = [],
  onInsert,
}: Props) => {
  const { uuid: currentGroupId } = useCurrentGroupContext();
  const { alertToast } = useToast();
  const groupId = currentGroupId || groupIdFromProps || "";

  const [localFolderId, setLocalFolderId] = useState("");
  const [selectedFiles, setSelectedFiles] = useState<SelecteFile[]>([]);

  const { data: folderWithTagFile, ...fileWithTagStates } =
    useGetTagDocumentsQuery(
      { groupId, tag_title: undefined },
      { skip: !groupId || !isOpen }
    );

  const { parents_list: tagParentList = [] } = folderWithTagFile || {};
  const serverFolderId = tagParentList.at(-1)?.uuid;
  const folderId = localFolderId || serverFolderId || "";

  const {
    data: folderData,
    refetch,
    ...folderStates
  } = useGetFolderContentQuery(
    { groupId, folderId },
    { skip: !folderId || !groupId || !isOpen }
  );

  const { files = [], subfolders = [], parents_list = [] } = folderData || {};
  const folderContent = [...subfolders, ...files];
  const isEmpty = folderContent.length === 0;
  const isLoading = folderStates.isLoading || fileWithTagStates.isLoading;
  const isSuccess = folderStates.isSuccess && fileWithTagStates.isSuccess;
  const isFetching = folderStates.isFetching || fileWithTagStates.isFetching;

  const {
    [parents_list.length - 1]: currentFolder,
    [parents_list.length - 2]: parentFolder,
  } = parents_list;

  const folderClicked = (newFolderId: string) => {
    try {
      setLocalFolderId(newFolderId);
      refetch().unwrap();
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  const toggleFile = (file: SelecteFile) => {
    setSelectedFiles((prev) => {
      const exists = prev.find((f) => f.uuid === file.uuid);
      if (exists) return prev.filter((f) => f.uuid !== file.uuid);
      return [...prev, file];
    });
  };

  const isSelected = (uuid: string) =>
    selectedFiles.some((f) => f.uuid === uuid) ||
    alreadySelectedUuids.includes(uuid);

  const handleInsert = () => {
    onInsert(selectedFiles);
    setSelectedFiles([]);
    close();
  };

  const handleClose = () => {
    setSelectedFiles([]);
    close();
  };

  const onBackButtonClick = () => {
    if (parentFolder?.uuid) {
      folderClicked(parentFolder.uuid);
    } else {
      handleClose();
    }
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={handleClose}>
      <Modal.Content size="regular">
        <Modal.Header>
          <Modal.Title>
            <div className="t-flex t-items-center t-gap-1">
              <Button
                customType="ghost_icon"
                size="small"
                onClick={onBackButtonClick}
                disabled={isFetching}
                isLoading={isFetching}
              >
                <img src={Prevfolder} alt="Back" />
              </Button>
              <span className="t-max-w-80 t-truncate t-text-body-sm">
                {currentFolder?.name}
              </span>
            </div>
          </Modal.Title>
          <Modal.Close />
        </Modal.Header>

        <Modal.Body>
          <Async.Root
            isEmpty={isEmpty}
            isLoading={isLoading}
            isSuccess={isSuccess}
          >
            <Async.Empty>
              <div className="t-flex t-justify-center t-items-center t-w-full t-text-neutral-40 t-h-80">
                This folder is empty
              </div>
            </Async.Empty>
            <Async.Success>
              <div className="t-flex t-h-80 t-flex-col t-items-start t-gap-1 t-select-none">
                {folderContent.map(
                  ({ uuid, name, file_type, type, is_previewable }) => {
                    const isFolder = type === FOLDER_TYPE;
                    const selected = isSelected(uuid);
                    const alreadyAdded = alreadySelectedUuids.includes(uuid);

                    return (
                      <Button
                        key={uuid}
                        customType="ghost"
                        onDoubleClick={() => isFolder && folderClicked(uuid)}
                        onClick={() => {
                          if (isFolder) return;
                          if (!alreadyAdded)
                            toggleFile({
                              uuid,
                              name,
                              file_type,
                              is_previewable,
                            });
                        }}
                        className={classNames(
                          "!t-flex !t-w-full !t-justify-start !t-items-center !t-gap-2 !t-px-2 !t-py-1",
                          {
                            "!t-border-purple-30 !t-bg-purple-10":
                              selected && !alreadyAdded,
                            "!t-opacity-40 !t-cursor-not-allowed": alreadyAdded,
                          }
                        )}
                      >
                        <FileIcon fileType={file_type} size="32" />
                        <div className="t-flex-1 t-max-w-[25rem] t-truncate t-text-body-sm">
                          {name}
                        </div>
                        {!isFolder && selected && !alreadyAdded && (
                          <GreentickGreenCircle className="t-text-green t-flex-shrink-0" />
                        )}
                        {alreadyAdded && (
                          <span className="t-text-body-xs t-text-text-30 t-flex-shrink-0">
                            Added
                          </span>
                        )}
                      </Button>
                    );
                  }
                )}
              </div>
            </Async.Success>
          </Async.Root>
        </Modal.Body>

        <Modal.Footer className="t-flex t-justify-between t-items-center">
          <div className="t-text-body-sm t-text-text-30">
            {selectedFiles.length > 0
              ? `${selectedFiles.length} file${
                  selectedFiles.length > 1 ? "s" : ""
                } selected`
              : "Click files to select"}
          </div>
          <Button
            customType="primary"
            size="small"
            onClick={handleInsert}
            disabled={selectedFiles.length === 0}
          >
            Insert {selectedFiles.length > 0 ? `(${selectedFiles.length})` : ""}
          </Button>
        </Modal.Footer>
      </Modal.Content>
    </Modal.Root>
  );
};
